/**
 * A single-select picker you can type into.
 *
 * `Select` is right for a handful of options. It is wrong the moment a list is long enough
 * that scanning it is the slow part: Radix's typeahead only matches from the start of an
 * option's text and resets after a pause, so on a list of a hundred Solutions in no
 * particular order the option you want is effectively unreachable — which, from the
 * outside, is indistinguishable from it not being there at all.
 *
 * So: a trigger that reads like a `SelectTrigger`, a search box that filters on every
 * token, and an alphabetical list that is never truncated. Keyboard-first — the search
 * field takes focus on open, arrows move the active option, Enter commits it.
 *
 * The active option is tracked by *value*, not by index, so narrowing the query can never
 * leave the highlight pointing at a row that is no longer there. That also means there is
 * no state to re-sync after a render, which is why this component has no effects.
 */

import { Check, ChevronDown, Search } from "lucide-react";
import { useId, useMemo, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { filterOptions, groupOptions, type SelectOption } from "@/lib/option-search";
import { cn } from "@/lib/utils";

interface Props extends Omit<React.ComponentProps<"button">, "value" | "onChange" | "children"> {
  options: readonly SelectOption[];
  /** The selected option's `value`, or "" for none. */
  value: string;
  onValueChange: (value: string) => void;
  /** Shown on the trigger while nothing is selected. */
  placeholder?: string;
  searchPlaceholder?: string;
  /** Shown in place of the list when the query matches nothing. */
  emptyMessage?: string;
  /** Options are still being fetched — the trigger says so instead of reading "none". */
  loading?: boolean;
  /**
   * Open the popover immediately when this instance mounts, instead of waiting for a
   * click on the trigger. Used for inline table-cell editing, where the click that swaps
   * the cell into its edit slot already expressed intent — a second click just to open
   * the picker would be an unnecessary extra step.
   */
  defaultOpen?: boolean;
  /**
   * Notified on every open-state change, including a commit (which closes the popover
   * directly, not through the trigger). Lets an inline-editing caller know when to fall
   * back from its edit slot to its display slot — this component has no other way to
   * signal "the popover just closed."
   */
  onOpenChange?: (open: boolean) => void;
}

export function SearchableSelect({
  options,
  value,
  onValueChange,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyMessage = "No matches.",
  loading = false,
  defaultOpen = false,
  onOpenChange,
  className,
  disabled,
  ...triggerProps
}: Props) {
  const [open, setOpen] = useState(defaultOpen);
  const [query, setQuery] = useState("");
  const [activeValue, setActiveValue] = useState(defaultOpen ? value : "");
  const listId = useId();
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Every place `open` changes also has to notify `onOpenChange` — `commit()` closes the
  // popover directly (not through `handleOpenChange`), so the notification lives here
  // rather than being folded into `handleOpenChange` alone.
  function updateOpen(next: boolean) {
    setOpen(next);
    onOpenChange?.(next);
  }

  const selected = options.find((option) => option.value === value);
  // The flat list is what the arrow keys walk; the grouped view is only for display, and
  // is derived from the same order, so a flat index still addresses the right row.
  const visible = useMemo(() => filterOptions(options, query), [options, query]);
  const grouped = useMemo(() => groupOptions(visible), [visible]);

  // Falls back to the first row whenever the active option has been filtered away.
  const foundIndex = visible.findIndex((option) => option.value === activeValue);
  const activeIndex = foundIndex === -1 ? 0 : foundIndex;

  /**
   * The list is unchanged by arrowing, so the row is already in the DOM. Looked up by id
   * rather than by child position, because group headings sit among the rows — and via
   * `getElementById` rather than a selector, because `useId` values are not valid CSS.
   */
  function revealRow(index: number) {
    listRef.current?.ownerDocument.getElementById(`${listId}-${index}`)?.scrollIntoView({
      block: "nearest",
    });
  }

  function handleOpenChange(next: boolean) {
    updateOpen(next);
    // Reopen on the current selection rather than at the top, so a chosen value is shown
    // instead of having to be found again.
    if (next) {
      setQuery("");
      setActiveValue(value);
    }
  }

  function commit(option: SelectOption | undefined) {
    if (option === undefined) return;
    onValueChange(option.value);
    updateOpen(false);
  }

  function move(step: number) {
    if (visible.length === 0) return;
    const next = (activeIndex + step + visible.length) % visible.length;
    setActiveValue(visible[next]?.value ?? "");
    revealRow(next);
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      move(1);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      move(-1);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      commit(visible[activeIndex]);
    }
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        disabled={disabled === true || loading}
        className={cn(
          "flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none transition-[color,box-shadow] dark:bg-input/30 dark:hover:bg-input/50",
          "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
          "aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40",
          "disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        {...triggerProps}
      >
        <span
          className={cn(
            "min-w-0 truncate text-left",
            selected === undefined && "text-muted-foreground",
          )}
        >
          {loading ? "Loading…" : (selected?.label ?? placeholder)}
          {selected?.group !== undefined && selected?.group !== null && (
            <span className="text-muted-foreground"> · {selected.group}</span>
          )}
        </span>
        <ChevronDown className="size-4 shrink-0 opacity-50" />
      </PopoverTrigger>

      <PopoverContent
        align="start"
        // Matches the trigger's width for a normal full-width form field, but never
        // narrower than a realistic option label. Without the floor, a narrow trigger
        // (e.g. this component driven inline from a table cell via defaultOpen) binds
        // the popover to `--radix-popover-trigger-width` and truncates every option's
        // `truncate`-ellipsized label mid-name, even though there's room to spare —
        // `min-w-*` only ever widens past `w-*`, so this can't change behavior for an
        // already-wide trigger.
        className="w-(--radix-popover-trigger-width) min-w-56 gap-0 p-0"
        onOpenAutoFocus={(event) => {
          // The search field owns focus — letting Radix move it to the content would eat
          // the first keystroke. The content is mounted by now, so the selected row can
          // also be scrolled to.
          event.preventDefault();
          inputRef.current?.focus();
          revealRow(activeIndex);
        }}
      >
        <div className="flex items-center gap-2 border-b border-border px-3">
          <Search className="size-3.5 shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder={searchPlaceholder}
            aria-controls={listId}
            aria-activedescendant={
              visible[activeIndex] === undefined ? undefined : `${listId}-${activeIndex}`
            }
            className="h-9 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>

        {visible.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-muted-foreground">{emptyMessage}</p>
        ) : (
          <div
            ref={listRef}
            id={listId}
            role="listbox"
            className="max-h-[min(320px,50vh)] overflow-y-auto p-1"
          >
            {grouped.map((section) => (
              // `role="presentation"` keeps the wrapper out of the accessibility tree, so
              // the options still read as direct children of the listbox — while giving
              // each sticky heading its own containing block, so a heading scrolls away
              // with its own group instead of staying pinned over the next one's rows.
              <div key={section.group ?? "__ungrouped"} role="presentation">
                {section.group !== null && (
                  // Sticky, because the heading is the only thing naming the Account once
                  // you have scrolled a long list. Hidden from assistive tech, which gets
                  // the Account from each option's own label instead — that keeps the
                  // options direct children of the listbox, where they belong.
                  <div
                    aria-hidden="true"
                    className="sticky top-0 z-10 bg-popover px-2 pt-1.5 pb-1 text-xs font-semibold tracking-wider text-muted-foreground uppercase"
                  >
                    {section.group}
                  </div>
                )}
                {section.options.map((option) => {
                  // Flat position, so the arrow keys and this render agree.
                  const index = visible.indexOf(option);
                  return (
                    <button
                      key={option.value}
                      id={`${listId}-${index}`}
                      type="button"
                      role="option"
                      tabIndex={-1}
                      aria-selected={option.value === value}
                      aria-label={
                        section.group === null ? option.label : `${option.label}, ${section.group}`
                      }
                      onClick={() => commit(option)}
                      onMouseEnter={() => setActiveValue(option.value)}
                      className={cn(
                        "flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-2 text-left text-sm",
                        index === activeIndex && "bg-accent text-accent-foreground",
                      )}
                    >
                      <Check
                        className={cn(
                          "size-3.5 shrink-0 text-muted-foreground",
                          option.value === value ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <span className="min-w-0 truncate">{option.label}</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        {options.length > 0 && (
          <p className="border-t border-border px-3 py-1.5 text-xs text-muted-foreground">
            {visible.length === options.length
              ? `${options.length} available`
              : `${visible.length} of ${options.length}`}
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}
